import {EditableReferencesTable} from './ui-components';
import {Text, TouchableOpacity, View} from '@/components/react-native';
import useAirportNote from '@/stores/airport-note';
import {mapFleetName} from '@/stores/fleet';
import {IReference} from '@/types/airport';
import {memo, useCallback, useEffect, useMemo, useState} from 'react';

// Fleet Management Component
const FleetManagement = memo(
  ({
    fleetType,
    fleetReferences,
    onFleetUpdate,
    preview
  }: {
    fleetType: string;
    fleetReferences: IReference[];
    onFleetUpdate: (fleetType: string, references: IReference[]) => void;
    preview?: boolean;
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [localReferences, setLocalReferences] =
      useState<IReference[]>(fleetReferences);

    const handleEditToggle = useCallback(() => {
      setIsEditing(!isEditing);
      if (!isEditing) {
        setLocalReferences(fleetReferences);
      }
    }, [isEditing, fleetReferences]);

    const handleSave = useCallback(() => {
      onFleetUpdate(fleetType, localReferences);
      setIsEditing(false);
    }, [fleetType, localReferences, onFleetUpdate]);

    const handleCancel = useCallback(() => {
      setLocalReferences(fleetReferences);
      setIsEditing(false);
    }, [fleetReferences]);

    const handleReferencesChange = useCallback((references: IReference[]) => {
      setLocalReferences(references);
    }, []);

    const fleetTitle = (fleet: string) => {
      return mapFleetName(fleet);
    };

    const handleAddReference = useCallback(() => {
      setLocalReferences([
        ...localReferences,
        {
          header: '',
          content: ''
        }
      ]);
      setIsEditing(true);
    }, []);

    useEffect(() => {
      setLocalReferences(fleetReferences);
    }, [fleetReferences]);

    return (
      <View className="mb-[16px]">
        <Text
          fontFamily="Geist"
          className="text-[#E7E5E4] text-[16px] font-[500] tracking-[-0.32px] uppercase"
        >
          {fleetTitle(fleetType)}
        </Text>
        {!localReferences || localReferences?.length === 0 ? (
          // Show empty state only when not editing
          !isEditing ? (
            <View className="py-[16px]">
              <Text
                fontFamily="Geist"
                className="text-[#78716C] text-[16px] font-[400] tracking-[-0.32px]"
              >
                No fleet specific information
              </Text>
              <TouchableOpacity
                className="flex-row items-center gap-[8px] px-[16px] py-[12px] bg-[rgba(112,112,112,0.2)] border border-dashed border-[#707070] rounded-[4px] mt-[8px]"
                onClick={handleAddReference}
              >
                <Text className="text-[#38BDF8] text-[20px] font-bold leading-none">
                  +
                </Text>
                <Text
                  fontFamily="Geist"
                  className="text-[#38BDF8] text-[16px] font-[500] tracking-[-0.32px]"
                >
                  Add Information
                </Text>
              </TouchableOpacity>
            </View>
          ) : (
            // Show EditableReferencesTable even when empty if editing
            <EditableReferencesTable
              references={localReferences}
              isEditing={isEditing}
              onReferencesChange={handleReferencesChange}
              onEditToggle={handleEditToggle}
              onSave={handleSave}
              onCancel={handleCancel}
              preview={preview}
              showHeader={false}
            />
          )
        ) : (
          <EditableReferencesTable
            references={localReferences}
            isEditing={isEditing}
            onReferencesChange={handleReferencesChange}
            onEditToggle={handleEditToggle}
            onSave={handleSave}
            onCancel={handleCancel}
            preview={preview}
            showHeader={false}
          />
        )}
      </View>
    );
  }
);
FleetManagement.displayName = 'FleetManagement';

type Props = {
  onFleetUpdate?: (fleetType: string, references: IReference[]) => void;
  fleetTypes?: string[];
  backgroundColor?: string;
  preview?: boolean;
};
const FleetInformation = ({
  onFleetUpdate,
  fleetTypes,
  backgroundColor,
  preview
}: Props) => {
  const airportInfo = useAirportNote((state) => state.airportNoteInfo);
  // Fleet information from category - only for Arrival and Departure
  const fleet = useMemo(() => {
    if (!airportInfo) return {} satisfies Record<string, IReference[]>;
    const temp = {};
    fleetTypes?.forEach((item) => {
      const fleetData: any = airportInfo?.categories?.fleet_info?.fleet ?? {};
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error
      // @ts-ignore
      temp[mapFleetName(item)] = fleetData[mapFleetName(item)] ?? [];
    });
    return temp;
  }, [airportInfo, fleetTypes]);

  const handleFleetUpdate = useCallback(
    (fleetType: string, references: IReference[]) => {
      onFleetUpdate?.(fleetType, references);
    },
    [onFleetUpdate]
  );
  return (
    <View className="py-[8px]" style={{backgroundColor}}>
      <View className="px-[16px] py-[18px]">
        {Object.entries(fleet).map(([fleetType, fleetReferences]) => (
          <FleetManagement
            key={fleetType}
            fleetType={fleetType}
            fleetReferences={fleetReferences as IReference[]}
            onFleetUpdate={handleFleetUpdate}
            preview={preview}
          />
        ))}
      </View>
    </View>
  );
};

export default FleetInformation;
